/**
 * Spatial soccer SFX. Respects the same sound / game-SFX volume keys as the rest of CursorGame.
 */

const audioCtx = new (window.AudioContext ||
  (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
const master = audioCtx.createGain();
master.connect(audioCtx.destination);

let lastHitAt = 0;
let lastPickupAt = 0;

function soundsOn(): boolean {
  return localStorage.getItem("settingSounds") !== "0";
}

function sfxPeak(base: number): number {
  if (!soundsOn()) return 0;
  const raw = localStorage.getItem("settingGameSfxVolume");
  const n = raw != null && raw !== "" ? Number(raw) : 6;
  const v = Number.isFinite(n) ? n : 6;
  return base * (Math.max(0, Math.min(10, v)) / 10);
}

export function resumeSoccerAudio(): void {
  if (audioCtx.state !== "running") void audioCtx.resume().catch(() => {});
}

function panFor(nx: number): number {
  return Math.max(-0.92, Math.min(0.92, (nx - 0.5) * 1.7));
}

function playTone(
  freq: number,
  dur: number,
  peak: number,
  type: OscillatorType,
  pan: number,
  slide = 0,
): void {
  if (!peak || audioCtx.state !== "running") return;
  const t0 = audioCtx.currentTime + 0.004;
  const osc = audioCtx.createOscillator();
  const gain = audioCtx.createGain();
  const panner = audioCtx.createStereoPanner();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t0);
  if (slide) osc.frequency.exponentialRampToValueAtTime(Math.max(40, freq + slide), t0 + dur);
  panner.pan.setValueAtTime(pan, t0);
  gain.gain.setValueAtTime(0.0001, t0);
  gain.gain.exponentialRampToValueAtTime(Math.max(0.0001, peak), t0 + 0.012);
  gain.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  osc.connect(gain);
  gain.connect(panner);
  panner.connect(master);
  osc.start(t0);
  osc.stop(t0 + dur + 0.03);
}

function playNoise(dur: number, peak: number, pan: number, hp = 400): void {
  if (!peak || audioCtx.state !== "running") return;
  const t0 = audioCtx.currentTime + 0.003;
  const n = audioCtx.createBuffer(1, Math.floor(audioCtx.sampleRate * dur), audioCtx.sampleRate);
  const data = n.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
  const src = audioCtx.createBufferSource();
  src.buffer = n;
  const filter = audioCtx.createBiquadFilter();
  filter.type = "highpass";
  filter.frequency.value = hp;
  const gain = audioCtx.createGain();
  const panner = audioCtx.createStereoPanner();
  panner.pan.value = pan;
  gain.gain.setValueAtTime(peak, t0);
  gain.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  src.connect(filter);
  filter.connect(gain);
  gain.connect(panner);
  panner.connect(master);
  src.start(t0);
  src.stop(t0 + dur + 0.02);
}

export function playSoccerHit(viewX: number, intensity: number): void {
  const now = performance.now();
  if (now - lastHitAt < 55) return;
  lastHitAt = now;
  resumeSoccerAudio();
  const peak = sfxPeak(0.045 + intensity * 0.07);
  const pan = panFor(viewX);
  playTone(180 + intensity * 90, 0.07, peak, "triangle", pan, 40);
  playNoise(0.05, peak * 0.55, pan, 700);
  if (intensity > 0.62) playTone(420 + intensity * 80, 0.09, peak * 0.7, "square", pan, -80);
}

export function playSoccerWall(viewX: number, intensity: number): void {
  resumeSoccerAudio();
  const peak = sfxPeak(0.028 + intensity * 0.04);
  playTone(140, 0.06, peak, "sine", panFor(viewX), -30);
  playNoise(0.04, peak * 0.4, panFor(viewX), 300);
}

export function playSoccerPost(viewX: number): void {
  resumeSoccerAudio();
  const peak = sfxPeak(0.09);
  const pan = panFor(viewX);
  playTone(880, 0.12, peak, "square", pan, -220);
  playTone(1320, 0.08, peak * 0.5, "triangle", pan, -160);
}

export function playSoccerGoal(): void {
  resumeSoccerAudio();
  const peak = sfxPeak(0.16);
  playTone(196, 0.45, peak, "sawtooth", 0, 80);
  playTone(247, 0.5, peak * 0.7, "square", -0.15, 60);
  playTone(330, 0.55, peak * 0.55, "triangle", 0.15, 40);
  playNoise(0.22, peak * 0.35, 0, 200);
}

export function playSoccerCountdown(n: number | "go"): void {
  resumeSoccerAudio();
  const peak = sfxPeak(0.07);
  if (n === "go") {
    playTone(523, 0.22, peak, "square", 0, 80);
    playTone(784, 0.18, peak * 0.6, "triangle", 0, 40);
    return;
  }
  playTone(330 + n * 40, 0.12, peak * 0.7, "sine", 0, 20);
}

export function playSoccerTimeWarn(): void {
  resumeSoccerAudio();
  playTone(392, 0.14, sfxPeak(0.05), "triangle", 0, 30);
}

export function playSoccerPickupMax(): void {
  const now = performance.now();
  if (now - lastPickupAt < 120) return;
  lastPickupAt = now;
  resumeSoccerAudio();
  const peak = sfxPeak(0.11);
  playTone(440, 0.14, peak, "sine", 0, 120);
  playTone(660, 0.18, peak * 0.75, "triangle", 0, 80);
  playTone(880, 0.22, peak * 0.55, "square", 0.1, 40);
}

export function playSoccerPickupSmall(): void {
  const now = performance.now();
  if (now - lastPickupAt < 120) return;
  lastPickupAt = now;
  resumeSoccerAudio();
  const peak = sfxPeak(0.07);
  playTone(520, 0.1, peak, "triangle", 0, 60);
  playTone(780, 0.12, peak * 0.65, "sine", 0.05, 40);
}

export function playSoccerMessage(kind: string): void {
  resumeSoccerAudio();
  const peak = sfxPeak(0.08);
  if (kind === "save") playTone(620, 0.16, peak, "triangle", 0, 90);
  else if (kind === "shot" || kind === "great_shot") playTone(490, 0.12, peak * 0.85, "square", 0, 70);
  else if (kind === "close_call") playTone(210, 0.14, peak * 0.7, "sawtooth", 0, 40);
  else if (kind === "overtime") {
    playTone(196, 0.2, peak, "square", 0, 50);
    playTone(294, 0.24, peak * 0.7, "triangle", 0, 40);
  }
}
